import type {
  ScanCaptureDetails,
  ScanOcrParallelShadowEvidence,
  ScanOpenCvParallelShadowEvidence,
  ScanParallelRuntimeSoakSnapshot,
} from "../types/scanWorkflow";
import { fullFidelityPerformanceEvidence } from "./recognitionPerformanceContract";
import { evaluateParallelRuntimeReleaseCertification } from "./parallelRuntimeCertificationService";

const STORAGE_KEY = "materialentnahme.vo.parallel-runtime-soak.v1";
const CONTRACT_VERSION = "browser-parallel-runtime-soak-v1" as const;
const MAX_SAMPLES = 32;
const REQUIRED_WARM_FULL_FIDELITY_SAMPLES = 10;

type ShadowProfile = "opencv" | "ocr" | "criticalPathCandidateBatch";
type LifecycleProfile = "opencv" | "ocr";
type LifecycleEvent = "pagehide_terminated" | "visibility_hidden_terminated" | "runtime_error_recycle" | "pool_ready";

interface SoakSample {
  atUtc: string;
  recognitionRunId: string | null;
  fullFidelityEligible: boolean;
  shutterToStep2Ms: number | null;
  opencv: {
    status: ScanOpenCvParallelShadowEvidence["status"];
    parityPass: boolean | null;
    poolReused: boolean;
    effectiveWorkers: number;
    overlapRatio: number | null;
    fallbackReason: string | null;
  } | null;
  ocr: {
    status: ScanOcrParallelShadowEvidence["status"];
    parityPass: boolean | null;
    poolReused: boolean;
    effectiveWorkers: number;
    overlapRatio: number | null;
    fallbackReason: string | null;
  } | null;
  criticalPathCandidateBatch: {
    status: "skipped" | "pass" | "mismatch" | "error";
    parityPass: boolean | null;
    poolReused: boolean;
    effectiveWorkers: number;
    overlapRatio: number | null;
    fallbackReason: string | null;
  } | null;
}

interface PersistedSoakState {
  samples: SoakSample[];
  lifecycle: Record<LifecycleProfile, Record<LifecycleEvent, number>>;
  acceptance: {
    workerFailureRecovery: { attempts: number; passes: number; lastAtUtc: string | null; lastError: string | null };
    memoryPressureFallback: { attempts: number; passes: number; lastAtUtc: string | null; lastReason: string | null };
  };
}

const EMPTY_LIFECYCLE = (): PersistedSoakState["lifecycle"] => ({
  opencv: { pagehide_terminated: 0, visibility_hidden_terminated: 0, runtime_error_recycle: 0, pool_ready: 0 },
  ocr: { pagehide_terminated: 0, visibility_hidden_terminated: 0, runtime_error_recycle: 0, pool_ready: 0 },
});
const EMPTY_ACCEPTANCE = (): PersistedSoakState["acceptance"] => ({
  workerFailureRecovery: { attempts: 0, passes: 0, lastAtUtc: null, lastError: null },
  memoryPressureFallback: { attempts: 0, passes: 0, lastAtUtc: null, lastReason: null },
});

let memoryState: PersistedSoakState = { samples: [], lifecycle: EMPTY_LIFECYCLE(), acceptance: EMPTY_ACCEPTANCE() };
let loaded = false;

function safeLoad(): PersistedSoakState {
  if (loaded) return memoryState;
  loaded = true;
  if (typeof localStorage === "undefined") return memoryState;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return memoryState;
    const parsed = JSON.parse(raw) as Partial<PersistedSoakState>;
    const samples = Array.isArray(parsed.samples) ? parsed.samples.slice(-MAX_SAMPLES) as SoakSample[] : [];
    const lifecycle = parsed.lifecycle && typeof parsed.lifecycle === "object" ? parsed.lifecycle as PersistedSoakState["lifecycle"] : EMPTY_LIFECYCLE();
    const acceptance = parsed.acceptance && typeof parsed.acceptance === "object"
      ? { ...EMPTY_ACCEPTANCE(), ...parsed.acceptance } as PersistedSoakState["acceptance"]
      : EMPTY_ACCEPTANCE();
    memoryState = { samples, lifecycle, acceptance };
  } catch {
    memoryState = { samples: [], lifecycle: EMPTY_LIFECYCLE(), acceptance: EMPTY_ACCEPTANCE() };
  }
  return memoryState;
}

function safePersist(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryState));
  } catch {
    // Soak telemetry must never block Recognition or Step 2.
  }
}

function profileSummary(profile: ShadowProfile, samples: SoakSample[]) {
  const entries = samples.map((sample) => sample[profile]).filter((entry): entry is NonNullable<SoakSample[typeof profile]> => entry != null);
  const fullFidelityEntries = samples
    .filter((sample) => sample.fullFidelityEligible)
    .map((sample) => sample[profile])
    .filter((entry): entry is NonNullable<SoakSample[typeof profile]> => entry != null);
  return {
    samples: entries.length,
    fullFidelitySamples: fullFidelityEntries.length,
    passes: entries.filter((entry) => entry.status === "pass").length,
    mismatches: entries.filter((entry) => entry.status === "mismatch").length,
    errors: entries.filter((entry) => entry.status === "error").length,
    skipped: entries.filter((entry) => entry.status === "skipped").length,
    warmReusedFullFidelityPasses: fullFidelityEntries.filter((entry) => entry.status === "pass" && entry.parityPass === true && entry.poolReused).length,
    parallelOverlapFullFidelityPasses: fullFidelityEntries.filter((entry) => entry.status === "pass" && entry.parityPass === true && entry.effectiveWorkers >= 2 && (entry.overlapRatio ?? 0) > 0).length,
  };
}

export function getParallelRuntimeSoakSnapshot(): ScanParallelRuntimeSoakSnapshot {
  const state = safeLoad();
  const samples = state.samples.slice(-MAX_SAMPLES);
  const opencv = profileSummary("opencv", samples);
  const ocr = profileSummary("ocr", samples);
  const criticalPathCandidateBatch = profileSummary("criticalPathCandidateBatch", samples);
  const cleanParityWindow = opencv.mismatches === 0 && opencv.errors === 0
    && ocr.mismatches === 0 && ocr.errors === 0
    && criticalPathCandidateBatch.mismatches === 0 && criticalPathCandidateBatch.errors === 0;
  const warmFullFidelityReady = opencv.warmReusedFullFidelityPasses >= REQUIRED_WARM_FULL_FIDELITY_SAMPLES
    && ocr.warmReusedFullFidelityPasses >= REQUIRED_WARM_FULL_FIDELITY_SAMPLES;
  const criticalPathCandidateBatchReady = criticalPathCandidateBatch.warmReusedFullFidelityPasses >= REQUIRED_WARM_FULL_FIDELITY_SAMPLES;
  const backgroundRecoveryEvidence = state.lifecycle.opencv.visibility_hidden_terminated > 0 || state.lifecycle.ocr.visibility_hidden_terminated > 0;
  const workerFailureRecoveryEvidence = state.acceptance.workerFailureRecovery.passes > 0;
  const memoryPressureFallbackEvidence = state.acceptance.memoryPressureFallback.passes > 0;
  const localSoakReady = cleanParityWindow
    && warmFullFidelityReady
    && criticalPathCandidateBatchReady
    && backgroundRecoveryEvidence
    && workerFailureRecoveryEvidence
    && memoryPressureFallbackEvidence;
  const releaseCertification = evaluateParallelRuntimeReleaseCertification();
  const releaseProfileMatched = releaseCertification.status === "certified" && releaseCertification.matched;
  // The 10/10 window is release/developer qualification evidence, not an end-user
  // onboarding requirement. A frozen certified device/browser profile may replace
  // the local historical soak; current-session admission/probe/warm-pool checks
  // remain mandatory in parallelProductionRuntimeService. Any locally observed
  // parity mismatch still fails closed even when a release profile is certified.
  // Legacy field name retained in the persisted/debug schema: from PV-11 onward
  // this means "historical QA qualification complete", NOT "the end user is
  // blocked until 10 scans". Actual Production activation is runtime-first in
  // parallelProductionRuntimeService (admission + measured probe + warm pools).
  const productionActivationAllowed = cleanParityWindow && (localSoakReady || releaseProfileMatched);
  const qualificationSource = releaseProfileMatched ? "release_certificate" : localSoakReady ? "local_development_soak" : "none";
  return {
    schemaVersion: 1,
    contractVersion: CONTRACT_VERSION,
    maxSamples: MAX_SAMPLES,
    requiredWarmFullFidelitySamples: REQUIRED_WARM_FULL_FIDELITY_SAMPLES,
    totalSamples: samples.length,
    fullFidelitySamples: samples.filter((sample) => sample.fullFidelityEligible).length,
    opencv,
    ocr,
    criticalPathCandidateBatch,
    lifecycle: structuredClone(state.lifecycle),
    acceptance: structuredClone(state.acceptance),
    qualification: {
      contractVersion: releaseCertification.contractVersion,
      releaseStatus: releaseCertification.status,
      releaseProfileMatched,
      releaseProfileId: releaseCertification.profileId,
      localSoakReady,
      source: qualificationSource,
      perUserSoakRequired: false,
      reason: releaseProfileMatched ? "RELEASE_PROFILE_CERTIFIED" : localSoakReady ? "LOCAL_DEVELOPMENT_SOAK_QUALIFIED" : releaseCertification.reason,
    },
    gates: {
      cleanParityWindow,
      warmFullFidelityReady,
      criticalPathCandidateBatchReady,
      backgroundRecoveryEvidence,
      workerFailureRecoveryEvidence,
      memoryPressureFallbackEvidence,
      productionActivationAllowed,
      state: !cleanParityWindow
        ? "blocked"
        : productionActivationAllowed
          ? "ready"
          : (warmFullFidelityReady && criticalPathCandidateBatchReady)
            ? "soak_candidate"
            : "collecting",
    },
  };
}

export function recordParallelRuntimeLifecycle(profile: LifecycleProfile, event: LifecycleEvent): void {
  const state = safeLoad();
  state.lifecycle[profile][event] = (state.lifecycle[profile][event] ?? 0) + 1;
  safePersist();
}

export function recordParallelRuntimeWorkerFailureAcceptance(input: { passed: boolean; error?: string | null }): void {
  const state = safeLoad();
  state.acceptance.workerFailureRecovery.attempts += 1;
  if (input.passed) state.acceptance.workerFailureRecovery.passes += 1;
  state.acceptance.workerFailureRecovery.lastAtUtc = new Date().toISOString();
  state.acceptance.workerFailureRecovery.lastError = input.error ?? null;
  safePersist();
}

export function recordParallelRuntimeMemoryPressureAcceptance(input: { passed: boolean; reason?: string | null }): void {
  const state = safeLoad();
  state.acceptance.memoryPressureFallback.attempts += 1;
  if (input.passed) state.acceptance.memoryPressureFallback.passes += 1;
  state.acceptance.memoryPressureFallback.lastAtUtc = new Date().toISOString();
  state.acceptance.memoryPressureFallback.lastReason = input.reason ?? null;
  safePersist();
}

export function recordParallelRuntimeSoakScan(input: {
  scanDetails: ScanCaptureDetails | null;
  openCvShadow: ScanOpenCvParallelShadowEvidence | null;
  ocrShadow: ScanOcrParallelShadowEvidence | null;
}): ScanParallelRuntimeSoakSnapshot {
  const state = safeLoad();
  const performance = fullFidelityPerformanceEvidence(input.scanDetails);
  const sample: SoakSample = {
    atUtc: new Date().toISOString(),
    recognitionRunId: input.scanDetails?.recognitionTiming?.recognitionRunId ?? null,
    fullFidelityEligible: performance.eligible,
    shutterToStep2Ms: performance.duration_ms,
    opencv: input.openCvShadow ? {
      status: input.openCvShadow.status,
      parityPass: input.openCvShadow.parity.parallelParityPass,
      poolReused: input.openCvShadow.workerPoolReused,
      effectiveWorkers: input.openCvShadow.effectiveWorkers,
      overlapRatio: input.openCvShadow.overlapRatio,
      fallbackReason: input.openCvShadow.fallbackReason,
    } : null,
    ocr: input.ocrShadow ? {
      status: input.ocrShadow.status,
      parityPass: input.ocrShadow.parity.recognitionParityPass,
      poolReused: input.ocrShadow.workerPoolReused,
      effectiveWorkers: input.ocrShadow.effectiveWorkers,
      overlapRatio: input.ocrShadow.overlapRatio,
      fallbackReason: input.ocrShadow.fallbackReason,
    } : null,
    criticalPathCandidateBatch: input.openCvShadow?.criticalPathCandidateBatch ? {
      status: input.openCvShadow.criticalPathCandidateBatch.status,
      parityPass: input.openCvShadow.criticalPathCandidateBatch.parallelParityPass,
      poolReused: input.openCvShadow.workerPoolReused,
      effectiveWorkers: input.openCvShadow.effectiveWorkers,
      overlapRatio: input.openCvShadow.overlapRatio,
      fallbackReason: input.openCvShadow.criticalPathCandidateBatch.fallbackReason,
    } : null,
  };
  state.samples = [...state.samples, sample].slice(-MAX_SAMPLES);
  safePersist();
  return getParallelRuntimeSoakSnapshot();
}
